import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
const publicationsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/publications.json`);
const authorsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/authors.json`);
const institutionsData = require(`../data/${process.env.REACT_APP_PERSON_ID}/institutions.json`);
import PublicationsCharts from './PublicationsCharts';
import { getPersonalInfo } from '../utils/contentLoader';
import './Publications.css';

function Publications() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [publications, setPublications] = useState([]);
  const [filteredPublications, setFilteredPublications] = useState([]);
  const [publicationTypes, setPublicationTypes] = useState([]);
  const [years, setYears] = useState([]);
  // Store min and max years for reference
  // const [yearRange, setYearRange] = useState({ min: '', max: '' });
  
  const [selectedJournal, setSelectedJournal] = useState(searchParams.get('type') || '');
  const [selectedAuthors, setSelectedAuthors] = useState(
    searchParams.get('co-authors')?.split(',') || []
  );
  const [selectedInstitutions, setSelectedInstitutions] = useState(
    searchParams.get('institutions')?.split(',') || []
  );
  const [selectedCountries, setSelectedCountries] = useState(
    searchParams.get('countries')?.split(',') || []
  );
  const [yearSince, setYearSince] = useState(searchParams.get('since-year') || '');
  const [yearUntil, setYearUntil] = useState(searchParams.get('until-year') || '');
  const [activeTab, setActiveTab] = useState(searchParams.get('selected') === 'true' ? 'selected' : 'all');
  
  const [authorCounts, setAuthorCounts] = useState({});
  const [institutionCounts, setInstitutionCounts] = useState({});
  const [countryCounts, setCountryCounts] = useState({});
  const [journalCounts, setJournalCounts] = useState({});
  const [keywordCounts, setKeywordCounts] = useState({});
  
  const [authorMap, setAuthorMap] = useState({});
  const [institutionMap, setInstitutionMap] = useState({});
  const [showNetworkModal, setShowNetworkModal] = useState(false);
  const [featuredPublications, setFeaturedPublications] = useState([]);
  
  const [selectedKeywords, setSelectedKeywords] = useState([]);
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [showAllAuthors, setShowAllAuthors] = useState(false);
  const [showAllInstitutions, setShowAllInstitutions] = useState(false);
  const [showAllCountries, setShowAllCountries] = useState(false);
  
  // State for collapsible filter sections
  const [activeFilter, setActiveFilter] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Load publications from the JSON file
    const loadedPublications = publicationsData.publications;
    
    // Process publications to handle author aliases and special keywords
    loadedPublications.forEach(pub => {
      // Make sure each publication has the required fields
      pub.type = pub.type || 'Unknown';
      pub.authors = pub.authors || [];
      pub.authorNames = pub.authorNames || [];
      
      // Process keywords for special flags
      if (pub.keywords && Array.isArray(pub.keywords)) {
        // Check for DBLP keyword (case-insensitive)
        const dblpIndex = pub.keywords.findIndex(keyword => 
          keyword.toUpperCase() === 'DBLP');
        
        if (dblpIndex !== -1) {
          pub.isDBLP = true;
          // Remove DBLP from displayed keywords
          pub.keywords.splice(dblpIndex, 1);
        }
      }
    });
    
    setPublications(loadedPublications);
    setFilteredPublications(loadedPublications);
    
    // Create a map of author IDs to author objects
    const authorsMap = {};
    authorsData.authors.forEach(author => {
      authorsMap[author.id] = author;
      
      // Also map all aliases to the same author object
      if (author.aliases) {
        author.aliases.forEach(alias => {
          // This helps with searching by alias
          authorsMap[alias] = author;
        });
      }
    });
    setAuthorMap(authorsMap);
    
    // Create a map of institution IDs to institution objects
    const instMap = {};
    institutionsData.institutions.forEach(institution => {
      instMap[institution.id] = institution;
      instMap[institution.name] = institution; // Also map by name for backward compatibility
    });
    setInstitutionMap(instMap);
    
    // Extract unique publication types for filters
    const uniqueTypes = [...new Set(loadedPublications.map(pub => pub.type))].sort();
    
    // Extract unique years for the year filter
    const uniqueYears = [...new Set(loadedPublications.map(pub => pub.year))].sort((a, b) => b - a);
    
    // Extract keywords and count occurrences (case-insensitive)
    const keywordMap = {};
    const normalizedKeywordMap = {}; // Maps lowercase keywords to their preferred display form
    
    loadedPublications.forEach(pub => {
      if (pub.keywords && Array.isArray(pub.keywords)) {
        pub.keywords.forEach(keyword => {
          if (!keyword) return;
          
          const lowerKeyword = keyword.toLowerCase();
          
          // If we've seen this keyword before (case-insensitive)
          if (normalizedKeywordMap[lowerKeyword]) {
            // Use the existing display form and increment count
            const displayKeyword = normalizedKeywordMap[lowerKeyword];
            keywordMap[displayKeyword] = (keywordMap[displayKeyword] || 0) + 1;
          } else {
            // First time seeing this keyword, store the original case as display form
            normalizedKeywordMap[lowerKeyword] = keyword;
            keywordMap[keyword] = 1;
          }
        });
      }
    });
    setKeywordCounts(keywordMap);
    
    // Set min and max years
    // const minYearValue = Math.min(...uniqueYears);
    // const maxYearValue = Math.max(...uniqueYears);
    
    setPublicationTypes(uniqueTypes);
    setYears(uniqueYears);
    // setYearRange({ min: minYearValue.toString(), max: maxYearValue.toString() });
    setYearSince(''); // Default to "Any" for Since
    setYearUntil(''); // Default to "Any" for Until
    
    // Calculate initial counts
    calculateCounts(loadedPublications);
    
    // Check if we have an author filter from navigation state
    if (location.state?.authorFilter) {
      setSelectedAuthors([location.state.authorFilter]);
      // Clear the state to avoid reapplying filter on refresh
      window.history.replaceState({}, document.title);
    }
    
    // Extract featured publications from the main publications data
    const featured = loadedPublications
      .filter(pub => pub.featuredOrder !== undefined)
      .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999));
    
    setFeaturedPublications(featured);
    
    // No need to set selectedPublications anymore as we're using featuredPublications directly
  }, [location.state]);

  // Whenever filters change, update the URL
  useEffect(() => {
    const params = {};
    if (selectedJournal) params['type'] = selectedJournal;
    if (selectedAuthors.length > 0) params['co-authors'] = selectedAuthors.join(',');
    if (selectedInstitutions.length > 0) params['institutions'] = selectedInstitutions.join(',');
    if (selectedCountries.length > 0) params['countries'] = selectedCountries.join(',');
    if (yearSince) params['since-year'] = yearSince;
    if (yearUntil) params['until-year'] = yearUntil;
    if (activeTab === 'selected') params['selected'] = 'true';
    
    setSearchParams(params);
  }, [selectedJournal, selectedAuthors, selectedInstitutions, selectedCountries, yearSince, yearUntil, activeTab]);


  const calculateCounts = (pubs) => {
    // Calculate counts for authors, institutions, countries, and journals
    const authorCount = {};
    const institutionCount = {};
    const countryCount = {};
    const journalCount = {};
    
    pubs.forEach(pub => {
      // Count authors
      if (pub.authors) {
        pub.authors.forEach(authorWithInst => {
          const { authorId } = parseAuthorId(authorWithInst);
          // Count by base author ID (without institution)
          authorCount[authorId] = (authorCount[authorId] || 0) + 1;
        });
      }
      
      // Count institutions and countries
      if (pub.authors) {
        // Track which institutions and countries are already counted for this publication
        const countedInstitutions = new Set();
        const countedCountries = new Set();
        
        pub.authors.forEach(authorWithInst => {
          const { institutions } = parseAuthorId(authorWithInst);
          
          // Process each institution for this author
          institutions.forEach(institutionId => {
            // Count institution if not already counted for this publication
            if (!countedInstitutions.has(institutionId)) {
              institutionCount[institutionId] = (institutionCount[institutionId] || 0) + 1;
              countedInstitutions.add(institutionId);
              
              // Count country if institution exists in our map
              const country = institutionMap[institutionId]?.country;
              if (country && !countedCountries.has(country)) {
                countryCount[country] = (countryCount[country] || 0) + 1;
                countedCountries.add(country);
              }
            }
          });
        });
      }
      
      // Count journals/publication types
      if (pub.type) {
        journalCount[pub.type] = (journalCount[pub.type] || 0) + 1;
      }
    });
    
    setAuthorCounts(authorCount);
    setInstitutionCounts(institutionCount);
    setCountryCounts(countryCount);
    setJournalCounts(journalCount);
  };

  useEffect(() => {
    // Get all featured publications
    const allFeatured = publications.filter(pub => pub.featuredOrder !== undefined)
      .sort((a, b) => (a.featuredOrder || 999) - (b.featuredOrder || 999));
    
    // Apply the same filters as the main publications list
    let filtered = [...allFeatured];
    
    // Filter by publication type
    if (selectedJournal) {
      filtered = filtered.filter(pub => pub.type === selectedJournal);
    }
    
    // Filter by authors
    if (selectedAuthors.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Check if all selected authors are in this publication
        return selectedAuthors.every(selectedAuthorId => {
          return pub.authors.some(authorWithInst => {
            const { authorId } = parseAuthorId(authorWithInst);
            return authorId === selectedAuthorId;
          });
        });
      });
    }
    
    // Filter by institutions
    if (selectedInstitutions.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Get all institutions of authors in this publication
        const pubInstitutions = [];
        pub.authors.forEach(authorWithInst => {
          const { institutions } = parseAuthorId(authorWithInst);
          pubInstitutions.push(...institutions);
        });
        
        // Check if ANY of the selected institutions are represented
        return selectedInstitutions.some(institution => 
          pubInstitutions.includes(institution)
        );
      });
    }
    
    // Filter by countries
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Get all institutions of authors in this publication
        const pubInstitutions = [];
        pub.authors.forEach(authorWithInst => {
          const { institutions } = parseAuthorId(authorWithInst);
          pubInstitutions.push(...institutions);
        });
        
        // Get countries for these institutions
        const pubCountries = pubInstitutions
          .map(institutionId => institutionMap[institutionId]?.country)
          .filter(Boolean);
        
        // Check if ANY of the selected countries are represented
        return selectedCountries.some(country => 
          pubCountries.includes(country)
        );
      });
    }
    
    // Filter by keywords
    if (selectedKeywords.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.keywords || !Array.isArray(pub.keywords)) return false;
        
        // Convert publication keywords to lowercase for case-insensitive comparison
        const pubKeywordsLower = pub.keywords.map(k => k.toLowerCase());
        
        // Check if ANY of the selected keywords are in this publication
        return selectedKeywords.some(keyword => 
          pubKeywordsLower.includes(keyword.toLowerCase())
        );
      });
    }
    
    // Apply year range filters
    if (yearSince) {
      filtered = filtered.filter(pub => pub.year >= parseInt(yearSince));
    }
    
    if (yearUntil) {
      filtered = filtered.filter(pub => pub.year <= parseInt(yearUntil));
    }
    
    setFeaturedPublications(filtered);
  }, [publications, selectedJournal, selectedAuthors, selectedInstitutions, selectedCountries, selectedKeywords, yearSince, yearUntil, institutionMap]);  

  useEffect(() => {
    // Check if we need to scroll to a specific publication
    if (location.state?.scrollToPublication) {
      setTimeout(() => {
        const pubElement = document.getElementById(`pub-${location.state.scrollToPublication}`);
        if (pubElement) {
          pubElement.scrollIntoView({ behavior: 'smooth' });
          // Highlight the publication briefly
          pubElement.classList.add('highlight-publication');
          setTimeout(() => {
            pubElement.classList.remove('highlight-publication');
          }, 2000);
        }
        // Clear the state to avoid scrolling on subsequent renders
        window.history.replaceState({}, document.title);
      }, 100);
    }
  }, [location.state]);

  useEffect(() => {
    // Set page title
    const personalInfo = getPersonalInfo();
    document.title = `${personalInfo.name} - Publications`;
    
    return () => {
      // Reset title when component unmounts
      document.title = personalInfo.name;
    };
  }, []);

  useEffect(() => {
    // Apply filters
    let filtered = [...publications];
    
    // Filter by publication type instead of specific journal
    if (selectedJournal) {
      filtered = filtered.filter(pub => pub.type === selectedJournal);
    }
    
    // Change author filter to use AND logic instead of OR
    if (selectedAuthors.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Check if all selected authors are in this publication
        return selectedAuthors.every(selectedAuthorId => {
          return pub.authors.some(authorWithInst => {
            const { authorId } = parseAuthorId(authorWithInst);
            return authorId === selectedAuthorId;
          });
        });
      });
    }
    
    // Filter by institutions (coauthors from these institutions)
    if (selectedInstitutions.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Get all institutions of authors in this publication
        const pubInstitutions = [];
        pub.authors.forEach(authorWithInst => {
          const { institutions } = parseAuthorId(authorWithInst);
          pubInstitutions.push(...institutions);
        });
        
        // Check if ANY of the selected institutions are represented
        return selectedInstitutions.some(institution => 
          pubInstitutions.includes(institution)
        );
      });
    }
    
    // Filter by countries
    if (selectedCountries.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.authors) return false;
        
        // Get all institutions of authors in this publication
        const pubInstitutions = [];
        pub.authors.forEach(authorWithInst => {
          const { institutions } = parseAuthorId(authorWithInst);
          pubInstitutions.push(...institutions);
        });
        
        // Get countries for these institutions
        const pubCountries = pubInstitutions
          .map(institutionId => institutionMap[institutionId]?.country)
          .filter(Boolean);
        
        // Check if ANY of the selected countries are represented
        return selectedCountries.some(country => 
          pubCountries.includes(country)
        );
      });
    }
    
    // Filter by keywords (case-insensitive)
    if (selectedKeywords.length > 0) {
      filtered = filtered.filter(pub => {
        if (!pub.keywords || !Array.isArray(pub.keywords)) return false;
        
        // Convert publication keywords to lowercase for case-insensitive comparison
        const pubKeywordsLower = pub.keywords.map(k => k.toLowerCase());
        
        // Check if ANY of the selected keywords are in this publication (case-insensitive)
        return selectedKeywords.some(keyword => 
          pubKeywordsLower.includes(keyword.toLowerCase())
        );
      });
    }
    
    // Apply year range filters
    if (yearSince) {
      filtered = filtered.filter(pub => pub.year >= parseInt(yearSince));
    }
    
    if (yearUntil) {
      filtered = filtered.filter(pub => pub.year <= parseInt(yearUntil));
    }
    
    setFilteredPublications(filtered);
    
    // Recalculate counts based on filtered publications
    calculateCounts(filtered);
  }, [selectedJournal, selectedAuthors, selectedInstitutions, selectedCountries, selectedKeywords, yearSince, yearUntil, publications, authorMap, institutionMap]);

  const handleAuthorClick = (authorWithInst) => {
    const { authorId } = parseAuthorId(authorWithInst);
    if (selectedAuthors.includes(authorId)) {
      setSelectedAuthors(selectedAuthors.filter(id => id !== authorId));
    } else {
      setSelectedAuthors([...selectedAuthors, authorId]);
    }
  };
  
  const handleAuthorDetailClick = (authorWithInst, e) => {
    e.stopPropagation();
    // Navigate to publications with this author selected
    const { authorId } = parseAuthorId(authorWithInst);
    setSelectedAuthors([authorId]);
  };

  const handleInstitutionClick = (institution) => {
    if (selectedInstitutions.includes(institution)) {
      setSelectedInstitutions(selectedInstitutions.filter(inst => inst !== institution));
    } else {
      setSelectedInstitutions([...selectedInstitutions, institution]);
    }
  };

  const handleCountryClick = (country) => {
    if (selectedCountries.includes(country)) {
      setSelectedCountries(selectedCountries.filter(c => c !== country));
    } else {
      setSelectedCountries([...selectedCountries, country]);
    }
  };
  
  const handleKeywordClick = (keyword) => {
    // Check if the keyword is already selected (case-insensitive)
    const keywordIndex = selectedKeywords.findIndex(
      k => k.toLowerCase() === keyword.toLowerCase()
    );
    
    if (keywordIndex >= 0) {
      // Remove the keyword if it's already selected
      setSelectedKeywords(selectedKeywords.filter((_, i) => i !== keywordIndex));
    } else {
      // Add the keyword with its original case
      setSelectedKeywords([...selectedKeywords, keyword]);
    }
  };
  
  const toggleShowAllKeywords = () => {
    setShowAllKeywords(!showAllKeywords);
  };
  
  const toggleShowAllAuthors = () => {
    setShowAllAuthors(!showAllAuthors);
  };
  
  const toggleShowAllInstitutions = () => {
    setShowAllInstitutions(!showAllInstitutions);
  };
  
  const toggleShowAllCountries = () => {
    setShowAllCountries(!showAllCountries);
  };
  
  const toggleFilter = (filterName) => {
    setActiveFilter(activeFilter === filterName ? null : filterName);
  };

  const handleYearSinceChange = (e) => {
    setYearSince(e.target.value);
  };
  
  const handleYearUntilChange = (e) => {
    setYearUntil(e.target.value);
  };
  


  const createSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^\w\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
      .substring(0, 50); // Limit length
  };

  const handlePublicationClick = (publication) => {
    console.log("Clicked publication:", publication);
    
    // Special handling for PhD thesis
    if (publication.type === 'PhD thesis' || publication.type === 'PhD Thesis') {
      console.log("Navigating to PhD thesis page");
      navigate('/publications/phd');
      window.scrollTo(0, 0);
      return;
    }
    
    const slug = createSlug(publication.title);
    console.log(`Created slug: ${slug} for publication ID: ${publication.id}`);
    
    // Ensure we use a consistent separator that won't be confused with dashes in the ID
    // Use a more distinct separator like '__' instead of '--'
    navigate(`/publications/${publication.year}/${slug}__${publication.id}`);
    // Scroll to top when navigating to publication detail
    window.scrollTo(0, 0);
  };

  const resetFilters = () => {
    setSelectedJournal('');
    setSelectedAuthors([]);
    setSelectedInstitutions([]);
    setSelectedCountries([]);
    setSelectedKeywords([]);
    setYearSince('');
    setYearUntil('');
    setActiveFilter(null); // Close any open filter panel
    setSearchParams({});
  };

  // Parse author ID with institution(s)
  const parseAuthorId = (authorWithInst) => {
    // Format: "authorId:institution1,institution2"
    const parts = authorWithInst.split(':');
    const authorId = parts[0];
    const institutions = parts.length > 1 ? parts[1].split(',') : [];
    return { authorId, institutions };
  };

  const getAuthorName = (authorWithInst) => {
    const { authorId } = parseAuthorId(authorWithInst);
    return authorMap[authorId]?.name || authorId;
  };
  
  const getInstitutionName = (institutionId) => {
    return institutionMap[institutionId]?.name || institutionId;
  };
  
  const getInstitutionWebsite = (institutionId) => {
    return institutionMap[institutionId]?.website || '#';
  };
  
  const getAuthorInstitutions = (authorId) => {
    // Find all institutions this author has been affiliated with
    const authorInstitutions = new Set();
    
    publications.forEach(pub => {
      pub.authors.forEach(authorWithInst => {
        const { authorId: pubAuthorId, institutions } = parseAuthorId(authorWithInst);
        if (pubAuthorId === authorId) {
          institutions.forEach(inst => authorInstitutions.add(inst));
        }
      });
    });
    
    return Array.from(authorInstitutions).map(instId => getInstitutionName(instId)).join(', ');
  };

  return (
    <div className="publications-container">
      <h1>Publications ({publications.length})</h1>
      
      <div className="filters-container">
        <h3 className="filters-heading">Filters</h3>
        <div className="horizontal-filters">
          <div className="filter-tabs">
            <div 
              className={`filter-tab ${activeFilter === 'type' ? 'active' : ''}`} 
              onClick={() => toggleFilter('type')}
            >
              Type {selectedJournal && <span className="filter-indicator">•</span>}
            </div>
            
            <div 
              className={`filter-tab ${activeFilter === 'year' ? 'active' : ''}`} 
              onClick={() => toggleFilter('year')}
            >
              Year Range {(yearSince || yearUntil) && <span className="filter-indicator">•</span>}
            </div>
            
            <div 
              className={`filter-tab ${activeFilter === 'authors' ? 'active' : ''}`} 
              onClick={() => toggleFilter('authors')}
            >
              Co-authors ({Object.keys(authorCounts).length}) {selectedAuthors.length > 0 && <span className="filter-indicator">•</span>}
            </div>
            
            <div 
              className={`filter-tab ${activeFilter === 'institutions' ? 'active' : ''}`} 
              onClick={() => toggleFilter('institutions')}
            >
              Institutions ({Object.keys(institutionCounts).length}) {selectedInstitutions.length > 0 && <span className="filter-indicator">•</span>}
            </div>
            
            <div 
              className={`filter-tab ${activeFilter === 'countries' ? 'active' : ''}`} 
              onClick={() => toggleFilter('countries')}
            >
              Countries ({Object.keys(countryCounts).length}) {selectedCountries.length > 0 && <span className="filter-indicator">•</span>}
            </div>
            
            <div 
              className={`filter-tab ${activeFilter === 'keywords' ? 'active' : ''}`} 
              onClick={() => toggleFilter('keywords')}
            >
              Keywords {selectedKeywords.length > 0 && <span className="filter-indicator">•</span>}
            </div>
            
            {(selectedJournal || yearSince || yearUntil || selectedAuthors.length > 0 || 
              selectedInstitutions.length > 0 || selectedCountries.length > 0 || selectedKeywords.length > 0) && (
              <div className="filter-tab clear-all" onClick={resetFilters}>
                Clear All Filters
              </div>
            )}
          </div>
          
          {/* Filter content panels */}
          {activeFilter === 'type' && (
            <div className="filter-panel">
              <div className="filter-group">
                <label>Publication Type:</label>
                <select 
                  className="filter-select"
                  value={selectedJournal} 
                  onChange={(e) => setSelectedJournal(e.target.value)}
                >
                  <option value="">All Types</option>
                  {publicationTypes.map((type, index) => {
                    const count = journalCounts[type] || 0;
                    return count > 0 ? (
                      <option key={index} value={type}>
                        {type} ({count})
                      </option>
                    ) : null;
                  }).filter(Boolean)}
                </select>
              </div>
            </div>
          )}
          
          {activeFilter === 'year' && (
            <div className="filter-panel">
              <div className="filter-group year-range">
                <div className="year-inputs">
                  <div className="year-input">
                    <label htmlFor="year-since">Since:</label>
                    <select 
                      className="filter-select"
                      value={yearSince}
                      onChange={handleYearSinceChange}
                      aria-label="Filter publications since year"
                    >
                      <option value="">Any</option>
                      {years.map((year, index) => (
                        <option key={index} value={year.toString()}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="year-input">
                    <label htmlFor="year-until">Until:</label>
                    <select 
                      className="filter-select"
                      value={yearUntil}
                      onChange={handleYearUntilChange}
                      aria-label="Filter publications until year"
                    >
                      <option value="">Any</option>
                      {years.map((year, index) => (
                        <option key={index} value={year.toString()}>
                          {year}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {activeFilter === 'authors' && (
            <div className="filter-panel">
              <div className={`tag-cloud ${showAllAuthors ? 'show-all' : ''}`}>
              {Object.keys(authorCounts)
                  .sort((a, b) => authorCounts[b] - authorCounts[a])
                  .slice(0, showAllAuthors ? Infinity : 20)
                  .map((authorId, index) => {
                    const count = authorCounts[authorId] || 0;
                    return (
                      <div 
                        key={index} 
                        className={`tag ${selectedAuthors.includes(authorId) ? 'selected' : ''}`}
                        onClick={() => handleAuthorClick(authorId)}
                        title={`Institutions: ${getAuthorInstitutions(authorId)}`}
                      >
                        <span>{authorMap[authorId]?.name || authorId}&nbsp;({count})</span>
                        <span 
                          className="info-icon" 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAuthorDetailClick(authorId, e);
                          }}
                          title="View author details"
                        >
                          ℹ️
                        </span>
                      </div>
                    );
                  })}

                {Object.keys(authorCounts).length > 20 && (
                  <div 
                    className="tag more-tag"
                    onClick={toggleShowAllAuthors}
                  >
                    <span>{showAllAuthors ? 'Show Less' : 'Show More'}</span>
                  </div>
                )}
              </div>
              <small className="filter-note">Publications must include ALL selected authors (AND logic)</small>
            </div>
          )}
          
          {activeFilter === 'institutions' && (
            <div className="filter-panel">
              <div className={`tag-cloud ${showAllInstitutions ? 'show-all' : ''}`}>
                {Object.keys(institutionCounts)
                  .sort((a, b) => institutionCounts[b] - institutionCounts[a])
                  .slice(0, showAllInstitutions ? undefined : 20)
                  .map((institutionId, index) => {
                    const count = institutionCounts[institutionId] || 0;
                    return count > 0 ? (
                      <div 
                        key={index} 
                        className={`tag ${selectedInstitutions.includes(institutionId) ? 'selected' : ''}`}
                        onClick={() => handleInstitutionClick(institutionId)}
                        title={`Institution: ${getInstitutionName(institutionId)}`}
                      >
                        <span>{getInstitutionName(institutionId)}</span>&nbsp;<span>({count})</span>
                      </div>
                    ) : null;
                  }).filter(Boolean)}
                {Object.keys(institutionCounts).length > 20 && (
                  <div 
                    className="tag more-tag"
                    onClick={toggleShowAllInstitutions}
                  >
                    <span>{showAllInstitutions ? 'Show Less' : 'Show More'}</span>
                  </div>
                )}
              </div>
              <small className="filter-note">Shows publications with co-authors from selected institutions</small>
            </div>
          )}
          
          {activeFilter === 'countries' && (
            <div className="filter-panel">
              <div className={`tag-cloud ${showAllCountries ? 'show-all' : ''}`}>
                {Object.keys(countryCounts)
                  .sort((a, b) => countryCounts[b] - countryCounts[a])
                  .slice(0, showAllCountries ? undefined : 20)
                  .map((country, index) => {
                    const count = countryCounts[country] || 0;
                    return count > 0 ? (
                      <div 
                        key={index} 
                        className={`tag ${selectedCountries.includes(country) ? 'selected' : ''}`}
                        onClick={() => handleCountryClick(country)}
                        title={`Country: ${country}`}
                      >
                        <span>{country}</span>&nbsp;<span>({count})</span>
                      </div>
                    ) : null;
                  }).filter(Boolean)}
                {Object.keys(countryCounts).length > 20 && (
                  <div 
                    className="tag more-tag"
                    onClick={toggleShowAllCountries}
                  >
                    <span>{showAllCountries ? 'Show Less' : 'Show More'}</span>
                  </div>
                )}
              </div>
              <small className="filter-note">Shows publications with co-authors from selected countries</small>
            </div>
          )}
          
          {activeFilter === 'keywords' && (
            <div className="filter-panel">
              <div className={`tag-cloud ${showAllKeywords ? 'show-all' : ''}`}>
                {Object.entries(keywordCounts)
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, showAllKeywords ? undefined : 20)
                  .map(([keyword, count]) => (
                    <div 
                      key={keyword}
                      className={`tag ${selectedKeywords.includes(keyword) ? 'selected' : ''}`}
                      onClick={() => handleKeywordClick(keyword)}
                    >
                      <span>{keyword}</span>
                      <span className="count">({count})</span>
                    </div>
                  ))}
                {Object.keys(keywordCounts).length > 20 && (
                  <div 
                    className="tag more-tag"
                    onClick={toggleShowAllKeywords}
                  >
                    <span>{showAllKeywords ? 'Show Less' : 'Show More'}</span>
                  </div>
                )}
              </div>
              <small className="filter-note">Click on keywords to filter publications</small>
            </div>
          )}
        </div>
        
        <div className="visualization-section">
          <button 
            className="visualization-button"
            onClick={() => setShowNetworkModal(true)}
          >
            Show Co-authorship Network
          </button>
        </div>
      </div>
      
      {showNetworkModal && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ width: '80%', height: '80%', maxWidth: '80vw', maxHeight: '80vh' }}>
            <div className="modal-header">
              <h3>Co-authorship Network</h3>
              <div className="modal-controls">
                <span className="modal-info">Scroll to see the entire network</span>
                <button className="modal-close" onClick={() => setShowNetworkModal(false)}>×</button>
              </div>
            </div>
            <div className="modal-body" style={{ height: 'calc(100% - 60px)', overflow: 'auto' }}>
              <div className="network-container" style={{ width: '100%', height: '100%' }}>
                <PublicationsCharts filteredPublications={filteredPublications} />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="publications-tabs">
        <div className="tabs">
          <button 
            className={`tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            All Publications ({filteredPublications.length})
          </button>
          <button 
            className={`tab ${activeTab === 'selected' ? 'active' : ''}`}
            onClick={() => setActiveTab('selected')}
          >
            Selected Publications ({featuredPublications.length})
          </button>
        </div>
        
        <div className="publications-list">
          {activeTab === 'all' ? (
            <>
              <p>Showing {filteredPublications.length} of {publications.length} publications</p>
              
              {filteredPublications.map((pub) => (
                <div id={`pub-${pub.id}`} key={pub.id} className="publication-item" onClick={() => handlePublicationClick(pub)}>
                  <div className="publication-title">
                    {pub.title}
                    {pub.isBestPaperAward && <span className="best-paper-badge">Best Paper Award</span>}
                    {(pub.type === 'PhD thesis'||pub.type === 'PhD Thesis') && <span className="phd-thesis-badge">PhD Thesis</span>}
                    {pub.type === 'Standard' && <span className="standard-badge">IEEE Standard</span>}
                  </div>
                  <div className="publication-authors">
                    {pub.authors.map((authorWithInst, index) => {
                      const { institutions } = parseAuthorId(authorWithInst);
                      return (
                        <span key={index}>
                          <span 
                            className="author-link" 
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAuthorClick(authorWithInst);
                            }}
                          >
                            {getAuthorName(authorWithInst)}
                          </span>
                          {institutions.length > 0 && (
                            <span className="author-institution">
                              ({institutions.map((instId, instIndex) => (
                                <span key={instIndex}>
                                  <a 
                                    href={getInstitutionWebsite(instId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    className="institution-link"
                                  >
                                    {getInstitutionName(instId)}
                                  </a>
                                  {instIndex < institutions.length - 1 ? ', ' : ''}
                                </span>
                              ))})
                            </span>
                          )}
                          {index < pub.authors.length - 1 ? ', ' : ''}
                        </span>
                      );
                    })}
                  </div>
                  <div className="publication-journal">
                    {(pub.type === 'PhD thesis'||pub.type === 'PhD Thesis') ? (
                      <>
                        <strong>PhD thesis dissertation</strong>, University of Deusto, {pub.year}
                      </>
                    ) : pub.type === 'Standard' ? (
                      <>
                        <strong>IEEE Standard</strong>, {pub.year}
                        {pub.publisher && `, ${pub.publisher}`}
                      </>
                    ) : (
                      <>
                        {pub.journal}
                        {pub.publisher && !pub.journal && `${pub.publisher}`}
                        {pub.impactFactor && `, JCR IF=${pub.impactFactor}`}
                        {pub.isJCR && !pub.impactFactor && ', JCR-indexed'}
                        {pub.quartile && `, Q${pub.quartile}`}
                        {`, ${pub.year}`}
                        {pub.volume && `, Vol. ${pub.volume}`}
                        {pub.issue && `, Issue ${pub.issue}`}
                        {pub.pages && `, pp. ${pub.pages}`}
                      </>
                    )}
                  </div>
                  <div className="publication-links">
                    <a 
                      href={`https://scholar.google.com/scholar?q=${encodeURIComponent(pub.title)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="scholar-link"
                    >
                      <i className="ai ai-google-scholar"></i> Google Scholar
                    </a>
                    {pub.doi && (
                      <a 
                        href={`https://doi.org/${pub.doi}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="doi-link"
                      >
                        <i className="ai ai-doi"></i> {pub.doi}
                      </a>
                    )}
                    {pub.url && !pub.doi && (
                      <a 
                        href={pub.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="url-link"
                      >
                        <i className="fas fa-external-link-alt"></i> View Online
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <>
              <p>Showing {featuredPublications.length} of {publications.filter(p => p.featuredOrder !== undefined).length} selected publications</p>
              
              {featuredPublications.map((pub) => {
                
                return (
                  <div id={`pub-${pub.id}`} key={pub.id} className="publication-item" onClick={() => handlePublicationClick(pub)}>
                    <div className="publication-title">
                      {pub.title}
                      {pub.isBestPaperAward && <span className="best-paper-badge">Best Paper Award</span>}
                      {(pub.type === 'PhD thesis' || pub.type === 'PhD Thesis') && <span className="phd-thesis-badge">PhD Thesis</span>}
                      {pub.type === 'Standard' && <span className="standard-badge">IEEE Standard</span>}
                      {pub.featuredOrder && <span className="featured-order" style={{display: 'none'}}>{pub.featuredOrder}</span>}
                    </div>
                    <div className="publication-authors">
                      {pub.authors.map((authorWithInst, index) => {
                        const { institutions } = parseAuthorId(authorWithInst);
                        return (
                          <span key={index}>
                            <span 
                              className="author-link" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAuthorClick(authorWithInst);
                              }}
                            >
                              {getAuthorName(authorWithInst)}
                            </span>
                            {institutions.length > 0 && (
                              <span className="author-institution">
                                ({institutions.map((instId, instIndex) => (
                                  <span key={instIndex}>
                                    <a 
                                      href={getInstitutionWebsite(instId)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      onClick={(e) => e.stopPropagation()}
                                      className="institution-link"
                                    >
                                      {getInstitutionName(instId)}
                                    </a>
                                    {instIndex < institutions.length - 1 ? ', ' : ''}
                                  </span>
                                ))})
                              </span>
                            )}
                            {index < pub.authors.length - 1 ? ', ' : ''}
                          </span>
                        );
                      })}
                    </div>
                    <div className="publication-journal">
                      {(pub.type === 'PhD thesis'||pub.type === 'PhD Thesis') ? (
                      <>
                        <strong>PhD thesis dissertation</strong>, University of Deusto, {pub.year}
                      </>
                      ) : pub.type === 'Standard' ? (
                        <>
                          {pub.publisher && `${pub.publisher}, `}
                          {pub.title}
                          {`, ${pub.year}`}
                          {pub.isbn && `, ISBN: ${pub.isbn}`}
                        </>
                      ) : (
                        <>
                          {pub.journal}
                          {pub.publisher && !pub.journal && `${pub.publisher}`}
                          {pub.impactFactor && `, JCR IF=${pub.impactFactor}`}
                          {pub.isJCR && !pub.impactFactor && ', JCR-indexed'}
                          {pub.quartile && `, Q${pub.quartile}`}
                          {`, ${pub.year}`}
                          {pub.volume && `, Vol. ${pub.volume}`}
                          {pub.issue && `, Issue ${pub.issue}`}
                          {pub.pages && `, pp. ${pub.pages}`}
                        </>
                      )}
                    </div>
                    <div className="publication-links">
                      <a 
                        href={`https://scholar.google.com/scholar?q=${encodeURIComponent(pub.title)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="scholar-link"
                      >
                        <i className="ai ai-google-scholar"></i> Google Scholar
                      </a>
                      {pub.doi && (
                        <a 
                          href={`https://doi.org/${pub.doi}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="doi-link"
                        >
                          <i className="ai ai-doi"></i> {pub.doi}
                        </a>
                      )}
                      {pub.url && !pub.doi && (
                        <a 
                          href={pub.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="url-link"
                        >
                          <i className="fas fa-external-link-alt"></i> View Online
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default Publications;
